import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums } from "@how/database";
import { supabase } from "../../lib/supabase";

/**
 * Every church's own domains (ADR 0051). Platform members read `tenant_domains` under its policy; nothing here can
 * make a domain live, which only the platform's worker does from what Cloudflare says.
 */
export type DomainStatus = Enums<"domain_status">;

/** Which app an address serves: the website, or a sign-in address (ADR 0067 R5). */
export type TenantSurface = Enums<"tenant_surface">;

export const SURFACES: readonly TenantSurface[] = [
  "public_web",
  "staff_dashboard",
  "member_portal",
];

/** What each address is for, in the words the church's screens use (ADR 0067 S8). */
export const SURFACE_LABELS: Readonly<Record<TenantSurface, string>> = {
  public_web: "Website",
  staff_dashboard: "Staff sign-in",
  member_portal: "Member sign-in",
};

export function isTenantSurface(value: string): value is TenantSurface {
  return (SURFACES as readonly string[]).includes(value);
}

export interface DomainRow {
  readonly id: string;
  readonly hostname: string;
  readonly surface: TenantSurface;
  readonly status: DomainStatus;
  readonly is_primary: boolean;
  readonly cf_status: string | null;
  readonly cf_ssl_status: string | null;
  readonly last_error: string | null;
  readonly created_at: string;
  readonly verified_at: string | null;
  readonly tenant_id: string;
  readonly tenant: { readonly name: string; readonly slug: string } | null;
}

export type DomainSort = {
  readonly id: "hostname" | "status" | "created_at";
  readonly desc: boolean;
};

export interface DomainsQuery {
  readonly search: string;
  readonly status: DomainStatus | "";
  readonly surface: TenantSurface | "";
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly sort: DomainSort;
}

/** The church's words for each state, the same ones its own Domain page uses. */
export const DOMAIN_STATUS_LABELS: Readonly<Record<DomainStatus, string>> = {
  pending_verification: "Waiting for DNS",
  verifying: "Issuing certificate",
  active: "Live",
  failed: "Needs attention",
  disabled: "Turned off",
};
export const DOMAIN_STATUSES: readonly DomainStatus[] = [
  "pending_verification",
  "verifying",
  "active",
  "failed",
  "disabled",
];

export function isDomainStatus(value: string): value is DomainStatus {
  return (DOMAIN_STATUSES as readonly string[]).includes(value);
}

/** Custom domains across every church: searched, filtered, sorted and paged by the server. */
export function useDomains(q: DomainsQuery) {
  return useQuery({
    queryKey: ["custom-domains", q],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: DomainRow[]; total: number }> => {
      const from = q.pageIndex * q.pageSize;
      let query = supabase
        .from("tenant_domains")
        .select(
          "id, hostname, surface, status, is_primary, cf_status, cf_ssl_status, last_error, created_at, verified_at, tenant_id, tenant:tenants(name, slug)",
          { count: "exact" },
        )
        .eq("kind", "custom")
        .order(q.sort.id, { ascending: !q.sort.desc })
        .range(from, from + q.pageSize - 1);
      if (q.status !== "") query = query.eq("status", q.status);
      if (q.surface !== "") query = query.eq("surface", q.surface);
      if (q.search.trim().length > 0) {
        query = query.ilike("hostname", `%${q.search.trim().replace(/[%_,()]/g, "")}%`);
      }
      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data as unknown as DomainRow[], total: count ?? 0 };
    },
  });
}

export interface DomainCounts {
  readonly total: number;
  readonly live: number;
  readonly waiting: number;
  readonly attention: number;
}

/** How many custom domains there are and where they stand; Cloudflare includes the first 100 hostnames. */
export function useDomainCounts() {
  return useQuery({
    queryKey: ["custom-domains", "counts"],
    queryFn: async (): Promise<DomainCounts> => {
      const count = async (statuses: readonly DomainStatus[] | null): Promise<number> => {
        let query = supabase
          .from("tenant_domains")
          .select("id", { count: "exact", head: true })
          .eq("kind", "custom");
        if (statuses !== null) query = query.in("status", statuses);
        const { count: n, error } = await query;
        if (error) throw error;
        return n ?? 0;
      };
      const [total, live, waiting, attention] = await Promise.all([
        count(null),
        count(["active"]),
        count(["pending_verification", "verifying"]),
        count(["failed"]),
      ]);
      return { total, live, waiting, attention };
    },
  });
}

/** Puts a domain first in the worker's queue (`recheck_custom_domain`); a super admin's action. */
export function useRecheckDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {
      const { error } = await supabase.rpc("recheck_custom_domain", { p_domain_id: domainId });
      if (error) throw error;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["custom-domains"] }),
  });
}

/**
 * Why Check now was refused. A sign-in address is checked only by the church's owner or a super admin outside a
 * support session; anyone inside an active one is refused (ADR 0067 R5, the B8 rule).
 */
export function recheckErrorMessage(error: unknown): string {
  const e = (typeof error === "object" && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
  };
  const message = typeof e.message === "string" ? e.message : "";
  if (e.code === "42501" && /support session/.test(message)) {
    return "A sign-in address can't be checked while you have a support session open for this church. End the session, then try again.";
  }
  if (e.code === "42501") return "Only a super admin can ask for a check.";
  if (e.code === "P0002") return "That domain is no longer here. Refresh the page.";
  return "The domain was not queued. Try again.";
}

/** Cloudflare's included hostnames; beyond it each costs $0.10 a month (ADR 0051). */
export const INCLUDED_HOSTNAMES = 100;
